import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken, clearToken, getToken } from "lib/api";
import { useI18n, readLanguageCookie } from "i18n";

const AuthContext = createContext(null);

// Renew token every 15 minutes (token lasts 20 min)
const RENEW_INTERVAL_MS = 15 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const renewTimer = useRef(null);
  // O idioma da sessao segue o idioma efetivo do usuario (preferencia
  // individual -> padrao do sistema -> PT-BR, resolvido no backend).
  const { setLanguage, chooseLanguage, resetLanguage } = useI18n();

  // Idioma da sessao a partir do /api/auth/me/:
  //
  // 1. a conta tem idioma proprio (`language_preference`) -> ele manda sempre;
  // 2. sem preferencia gravada, o valor devolvido e so o padrao do sistema —
  //    e ai a escolha explicita do visitante (cookie) vale mais.
  const applySessionLanguage = useCallback(
    (data) => {
      if (data?.language_preference) {
        setLanguage(data.language_preference);
      } else if (!readLanguageCookie()) {
        setLanguage(data?.language);
      }
    },
    [setLanguage]
  );

  const stopRenewal = useCallback(() => {
    if (renewTimer.current) {
      clearInterval(renewTimer.current);
      renewTimer.current = null;
    }
  }, []);

  const renewToken = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await api.post("/api/auth/refresh/");
      if (res.data?.token) {
        setToken(res.data.token);
      }
    } catch {
      // Token expired or invalid — force logout
      stopRenewal();
      clearToken();
      setUser(null);
    }
  }, [stopRenewal]);

  const startRenewal = useCallback(() => {
    stopRenewal();
    renewTimer.current = setInterval(renewToken, RENEW_INTERVAL_MS);
  }, [renewToken, stopRenewal]);

  const fetchUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/api/auth/me/");
      setUser(res.data);
      applySessionLanguage(res.data);
      startRenewal();
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, [startRenewal, applySessionLanguage]);

  useEffect(() => {
    fetchUser();
    return stopRenewal;
  }, [fetchUser, stopRenewal]);

  const loginUser = useCallback((userData, newToken) => {
    setToken(newToken);
    setUser(userData);
    applySessionLanguage(userData);
    startRenewal();
  }, [startRenewal, applySessionLanguage]);

  // Troca de idioma feita pelo usuario LOGADO (seletor da console).
  //
  // So o cookie nao basta: todo carregamento resolve o idioma da sessao pelo
  // `effective_language` que o /api/auth/me/ devolve (regra do idioma do
  // destinatario), entao a escolha sumia no refresh. Aqui ela vira preferencia
  // da CONTA — e passa a valer tambem nos e-mails e em outro navegador.
  const changeLanguage = useCallback(async (value) => {
    chooseLanguage(value);
    if (!getToken()) return;
    try {
      const res = await api.patch("/api/auth/me/", { language: value });
      setUser(res.data);
    } catch {
      // Falha ao gravar no perfil nao desfaz a troca: a sessao (e o cookie)
      // seguem no idioma escolhido.
    }
  }, [chooseLanguage]);

  const logout = useCallback(() => {
    stopRenewal();
    clearToken();
    setUser(null);
    resetLanguage();
    navigate("/app/login");
  }, [navigate, stopRenewal, resetLanguage]);

  return (
    <AuthContext.Provider
      value={{ user, loading, loginUser, logout, fetchUser, changeLanguage }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
